import {
  ResearchProject,
  ResearchSession,
  DataSource,
  AnalysisResult,
  ResearchInsight,
  ResearchReport,
  ResearchAgentResult,
  AnalysisMethod,
  ResearchPhaseConfig,
  EvidenceItem,
  ReportSection,
} from '../models/researchStudio';
import { 
  AgentConfig, 
  ProjectContext,
  ProjectArtifact,
  PhaseStatus,
} from '../models/genericStudio';
import { RagTagService } from './ragTagService';
import { SimpleDataService } from './simpleDataService';
import { dataAnalysisService } from './dataAnalysisService';
import { AbstractThinkerService } from './abstractThinkerService';
import { ImageAnalysisService } from './imageAnalysisService';
import { BlogService } from './blogService';
import { TechDocService } from './techDocService';
import { JournalismService } from './journalismService';
import { WorkflowEngine } from './workflowEngine';

const RESEARCH_SESSION_KEY = 'research_studio_sessions';

export class ResearchStudioService {
  // Execute agent based on type and configuration
  static async executeAgent(
    agent: AgentConfig,
    context: ProjectContext
  ): Promise<ResearchAgentResult> {
    const project = context.project as ResearchProject;
    
    try {
      switch (agent.agentType) {
        case 'ragtag-upload':
          return await this.executeRagTagUpload(agent, project, context);
          
        case 'ragtag-query':
          return await this.executeRagTagQuery(agent, project, context);
          
        case 'ragtag-summarize':
          return await this.executeRagTagSummarize(agent, project, context);
          
        case 'simple-data-query':
          return await this.executeSimpleDataQuery(agent, project, context);
          
        case 'simple-data-summarize':
          return await this.executeSimpleDataSummarize(agent, project, context);
          
        case 'data-analysis-rss':
          return await this.executeRSSAnalysis(agent, project, context);
          
        case 'data-analysis-csv':
          return await this.executeCSVAnalysis(agent, project, context);
          
        case 'abstract-thinking':
          return await this.executeAbstractThinking(agent, project, context);
          
        case 'image-analysis':
          return await this.executeImageAnalysis(agent, project, context);
          
        case 'report-generation':
          return await this.executeReportGeneration(agent, project, context);
          
        case 'insight-synthesis':
          return await this.executeInsightSynthesis(agent, project, context);
          
        default:
          throw new Error(`Unknown agent type: ${agent.agentType}`);
      }
    } catch (error: any) {
      return {
        agentType: agent.agentType,
        success: false,
        data: {},
        error: error.message,
      };
    }
  }

  // RAG Tag Agents
  private static async executeRagTagUpload(
    agent: AgentConfig,
    project: ResearchProject,
    context: ProjectContext
  ): Promise<ResearchAgentResult> {
    const dataSources = project.dataSources.filter(ds => 
      ds.type === 'documents' && ds.status === 'pending'
    );

    const results: string[] = [];
    
    for (const source of dataSources) {
      const response = await RagTagService.upload({
        content: source.data || '',
        contentType: 'text',
        metadata: {
          title: source.name,
          tags: source.metadata.tags || [],
          source: source.metadata.url || 'user-upload',
        },
      });

      if (response.status === 'success') {
        source.status = 'collected';
        source.data = { contentId: response.contentId };
        results.push(response.contentId);
      }
    }

    return {
      agentType: agent.agentType,
      success: true,
      data: {
        contentId: results.join(','),
        summary: `Uploaded ${results.length} documents successfully`,
      },
    };
  }

  private static async executeRagTagQuery(
    agent: AgentConfig,
    project: ResearchProject,
    context: ProjectContext
  ): Promise<ResearchAgentResult> {
    const query = agent.config?.query || context.userInputs.researchQuestion || 
      project.keyQuestions[0] || 'What are the key findings?';
    
    const tags = this.getProjectTags(project);
    
    const response = await RagTagService.query({
      query,
      contentIds: tags,
      topK: 5,
      temperature: 0.7,
      maxTokens: agent.config?.maxResponseTokens || 1000,
    });

    return {
      agentType: agent.agentType,
      success: true,
      data: {
        analysis: response.answer,
        insights: this.extractInsights(response.answer),
        rawData: response.relevantChunks,
      },
    };
  }

  private static async executeRagTagSummarize(
    agent: AgentConfig,
    project: ResearchProject,
    context: ProjectContext
  ): Promise<ResearchAgentResult> {
    const tags = this.getProjectTags(project);
    const summaryType = agent.config?.summaryType || 'detailed';
    
    const response = await RagTagService.summarize({
      contentIds: tags,
      summaryType: summaryType as 'brief' | 'detailed' | 'executive',
      temperature: 0.7,
      maxTokens: agent.config?.maxResponseTokens || 1500,
    });

    return {
      agentType: agent.agentType,
      success: true,
      data: {
        summary: response.summary,
        insights: this.extractInsights(response.summary),
      },
    };
  }

  // Simple Data Agents
  private static async executeSimpleDataQuery(
    agent: AgentConfig,
    project: ResearchProject,
    context: ProjectContext
  ): Promise<ResearchAgentResult> {
    const dataSources = project.dataSources.filter(ds => 
      ds.type === 'user-provided' && ds.data
    );

    const content = dataSources.map(ds => ds.data).filter(Boolean);
    const query = agent.config?.query || context.userInputs.researchQuestion || 
      'What are the main points?';

    const response = await SimpleDataService.query({
      data: content.join('\n'),
      query,
      model: 'gpt-4',
      temperature: 0.7,
      maxTokens: agent.config?.maxTokens || 1000,
    });

    return {
      agentType: agent.agentType,
      success: true,
      data: {
        analysis: response.result,
        insights: this.extractInsights(response.result),
      },
    };
  }

  private static async executeSimpleDataSummarize(
    agent: AgentConfig,
    project: ResearchProject,
    context: ProjectContext
  ): Promise<ResearchAgentResult> {
    const dataSources = project.dataSources.filter(ds => 
      ds.type === 'user-provided' && ds.data
    );

    const content = dataSources.map(ds => ds.data).filter(Boolean);
    const summaryType = agent.config?.summaryType || 'detailed';

    const response = await SimpleDataService.summarize({
      data: content.join('\n'),
      summaryType: summaryType as 'brief' | 'detailed' | 'bullet_points',
      model: 'gpt-4',
      temperature: 0.7,
      maxTokens: agent.config?.maxTokens || 1500,
    });

    return {
      agentType: agent.agentType,
      success: true,
      data: {
        summary: response.result,
        insights: this.extractInsights(response.result),
      },
    };
  }

  // Data Analysis Agents
  private static async executeRSSAnalysis(
    agent: AgentConfig,
    project: ResearchProject,
    context: ProjectContext
  ): Promise<ResearchAgentResult> {
    const rssSources = project.dataSources.filter(ds => 
      ds.type === 'rss-feeds' && ds.metadata.url
    );

    const analyses: string[] = [];
    const insights: string[] = [];

    for (const source of rssSources) {
      const response = await dataAnalysisService.analyzeRss({
        rssUrl: source.metadata.url!,
        userPrompt: agent.config?.prompt || 
          `Analyze this RSS feed for ${project.researchObjectives.join(', ')}`,
        temperature: agent.config?.temperature || 0.7,
      });

      if (response.analysis) {
        analyses.push(response.analysis);
        insights.push(...(response.insights || []));
        source.status = 'analyzed';
        source.analysisResults = response;
      }
    }

    return {
      agentType: agent.agentType,
      success: true,
      data: {
        analysis: analyses.join('\n\n'),
        insights,
        recommendations: insights.slice(0, 5),
      },
    };
  }

  private static async executeCSVAnalysis(
    agent: AgentConfig,
    project: ResearchProject,
    context: ProjectContext
  ): Promise<ResearchAgentResult> {
    const csvSources = project.dataSources.filter(ds => 
      ds.type === 'csv-data' && ds.data
    );

    const analyses: string[] = [];
    const insights: string[] = [];

    for (const source of csvSources) {
      const response = await dataAnalysisService.analyzeDelimitedData({
        delimitedData: source.data,
        userPrompt: agent.config?.prompt || 
          `Analyze this data for ${project.researchObjectives.join(', ')}`,
        delimiter: agent.config?.delimiter || ',',
        hasHeaderRow: agent.config?.hasHeader !== false,
        temperature: agent.config?.temperature || 0.7,
      });

      if (response.analysis) {
        analyses.push(response.analysis);
        insights.push(...(response.insights || []));
        source.status = 'analyzed';
        source.analysisResults = response;
      }
    }

    return {
      agentType: agent.agentType,
      success: true,
      data: {
        analysis: analyses.join('\n\n'),
        insights,
        recommendations: [],
      },
    };
  }

  // Abstract Thinking Agent
  private static async executeAbstractThinking(
    agent: AgentConfig,
    project: ResearchProject,
    context: ProjectContext
  ): Promise<ResearchAgentResult> {
    // Gather all previous analyses
    const previousAnalyses = context.previousResults;
    const analysisTexts: string[] = [];
    
    previousAnalyses.forEach((result: any) => {
      if (result.data?.analysis) {
        analysisTexts.push(result.data.analysis);
      }
      if (result.data?.summary) {
        analysisTexts.push(result.data.summary);
      }
    });

    const topic = `Research synthesis for: ${project.researchObjectives.join(', ')}. 
      Key questions: ${project.keyQuestions.join(', ')}.
      Previous findings: ${analysisTexts.join(' ')}`;

    const response = await AbstractThinkerService.startThinking({
      initialQueryOrTopic: topic,
      maxTokensPerAgentTurn: agent.config?.maxTokens || 1500,
      iterations: agent.config?.iterations || 3,
    });

    return {
      agentType: agent.agentType,
      sessionId: response.sessionId,
      success: true,
      data: {
        analysis: response.finalConclusion || response.thinkingProcess?.map(t => t.output).join('\n') || '',
        insights: response.thinkingProcess?.map(t => t.output || '').filter(o => o) || [],
        summary: response.finalConclusion || '',
      },
    };
  }

  // Image Analysis Agent
  private static async executeImageAnalysis(
    agent: AgentConfig,
    project: ResearchProject,
    context: ProjectContext
  ): Promise<ResearchAgentResult> {
    const imageSources = project.dataSources.filter(ds => 
      ds.type === 'images' && (ds.data || ds.metadata.url)
    );

    const analyses: string[] = [];

    for (const source of imageSources) {
      const response = await ImageAnalysisService.analyzeImageUrl({
        imageUrl: source.metadata.url,
        imageBytes: source.data,
        userPrompt: agent.config?.prompt || 
          `Analyze this image in the context of ${project.researchObjectives.join(', ')}`,
        maxResponseTokens: agent.config?.maxTokens || 1000,
      });

      if (response.description) {
        analyses.push(response.description);
        source.status = 'analyzed';
        source.analysisResults = response;
      }
    }

    return {
      agentType: agent.agentType,
      success: true,
      data: {
        analysis: analyses.join('\n\n'),
        insights: this.extractInsights(analyses.join(' ')),
      },
    };
  }

  // Report Generation Agent
  private static async executeReportGeneration(
    agent: AgentConfig,
    project: ResearchProject,
    context: ProjectContext
  ): Promise<ResearchAgentResult> {
    const session = this.getCurrentSession(project.id);
    if (!session) {
      throw new Error('No active research session found');
    }

    // Use appropriate agent based on report type
    const reportType = agent.config?.reportType || 'technical';
    let report: ResearchReport;

    if (reportType === 'technical') {
      const response = await TechDocService.generateTechDoc({
        subject: project.name,
        docType: 'technical-report',
        technicalLevel: 'advanced',
        targetAudience: 'researchers',
        includeExamples: true,
      });

      report = this.convertToResearchReport(response, session);
    } else if (reportType === 'journalistic') {
      const response = await JournalismService.generateArticle({
        topic: project.name,
        style: 'analytical',
        toneGuidance: 'objective, data-driven',
        keyFacts: session.insights.map(i => i.description),
      });

      report = this.convertJournalismToReport(response, session);
    } else {
      // Use blog agent for general reports
      const response = await BlogService.generateBlogPost({
        topic: project.name,
        audience: 'professional',
        style: 'informative',
        keyPoints: session.insights.map(i => i.title),
        iterations: 2,
      });

      report = this.convertBlogToReport(response, session);
    }

    session.report = report;
    this.saveSession(session);

    return {
      agentType: agent.agentType,
      success: true,
      data: {
        summary: report.executiveSummary,
        rawData: report,
      },
    };
  }

  // Insight Synthesis Agent
  private static async executeInsightSynthesis(
    agent: AgentConfig,
    project: ResearchProject,
    context: ProjectContext
  ): Promise<ResearchAgentResult> {
    const session = this.getCurrentSession(project.id);
    if (!session) {
      throw new Error('No active research session found');
    }

    // Extract insights from all analyses
    const insights: ResearchInsight[] = [];
    const analysisResults = session.analyses;

    // Group related findings
    const insightGroups = this.groupAnalysesByTheme(analysisResults);

    // Generate insights for each group
    for (const [theme, analyses] of Object.entries(insightGroups)) {
      const insight = this.generateInsight(theme, analyses, project);
      insights.push(insight);
    }

    // Find patterns and correlations
    const patterns = this.findPatterns(insights, analysisResults);
    insights.push(...patterns);

    // Update session with insights
    session.insights = insights;
    this.saveSession(session);

    return {
      agentType: agent.agentType,
      success: true,
      data: {
        insights: insights.map(i => i.description),
        summary: `Generated ${insights.length} insights across ${Object.keys(insightGroups).length} themes`,
      },
    };
  }

  // Helper methods
  private static getProjectTags(project: ResearchProject): string[] {
    const tags: string[] = [];
    
    // Add tags from data sources
    project.dataSources.forEach(ds => {
      if (ds.metadata.tags) {
        tags.push(...ds.metadata.tags);
      }
    });

    // Add research type as tag
    tags.push(project.researchType);

    // Add unique tags only
    return Array.from(new Set(tags));
  }

  private static extractInsights(text: string): string[] {
    // Simple insight extraction - can be enhanced with NLP
    const insights: string[] = [];
    
    // Look for key phrases
    const keyPhrases = [
      /key finding[s]?:(.+?)(?:\.|$)/gi,
      /important(?:ly)?:(.+?)(?:\.|$)/gi,
      /significant(?:ly)?:(.+?)(?:\.|$)/gi,
      /notable:(.+?)(?:\.|$)/gi,
      /conclusion[s]?:(.+?)(?:\.|$)/gi,
    ];

    keyPhrases.forEach(pattern => {
      const matches = Array.from(text.matchAll(pattern));
      for (const match of matches) {
        if (match[1]) {
          insights.push(match[1].trim());
        }
      }
    });

    // Also split by bullet points or numbered lists
    const listItems = text.match(/^[\s]*[-•*]\s*(.+)$/gm);
    if (listItems) {
      insights.push(...listItems.map(item => item.replace(/^[\s]*[-•*]\s*/, '').trim()));
    }

    return insights.slice(0, 10); // Limit to top 10 insights
  }

  private static groupAnalysesByTheme(analyses: AnalysisResult[]): Record<string, AnalysisResult[]> {
    const groups: Record<string, AnalysisResult[]> = {};
    
    // Simple grouping by analysis type for now
    analyses.forEach(analysis => {
      const theme = analysis.analysisType;
      if (!groups[theme]) {
        groups[theme] = [];
      }
      groups[theme].push(analysis);
    });

    return groups;
  }

  private static generateInsight(
    theme: string,
    analyses: AnalysisResult[],
    project: ResearchProject
  ): ResearchInsight {
    // Aggregate findings
    const allInsights = analyses.flatMap(a => a.insights);
    const avgConfidence = analyses.reduce((sum, a) => sum + a.confidence, 0) / analyses.length;

    return {
      id: `insight-${Date.now()}-${Math.random()}`,
      type: 'finding',
      title: `${theme} Analysis Results`,
      description: allInsights.join('. '),
      supportingEvidence: analyses.map(a => ({
        sourceId: a.dataSourceId,
        sourceName: project.dataSources.find(ds => ds.id === a.dataSourceId)?.name || 'Unknown',
        excerpt: a.result.substring(0, 200) + '...',
        relevanceScore: a.confidence,
      })),
      confidence: avgConfidence,
      importance: avgConfidence > 0.8 ? 'high' : avgConfidence > 0.6 ? 'medium' : 'low',
      timestamp: new Date().toISOString(),
    };
  }

  private static findPatterns(
    insights: ResearchInsight[],
    analyses: AnalysisResult[]
  ): ResearchInsight[] {
    const patterns: ResearchInsight[] = [];
    
    // Look for recurring themes
    const themeCounts: Record<string, number> = {};
    insights.forEach(insight => {
      const words = insight.description.toLowerCase().split(/\s+/);
      words.forEach(word => {
        if (word.length > 5) { // Only count significant words
          themeCounts[word] = (themeCounts[word] || 0) + 1;
        }
      });
    });

    // Create pattern insights for recurring themes
    Object.entries(themeCounts)
      .filter(([_, count]) => count >= 3)
      .forEach(([theme, count]) => {
        patterns.push({
          id: `pattern-${Date.now()}-${theme}`,
          type: 'pattern',
          title: `Recurring Theme: ${theme}`,
          description: `The theme "${theme}" appears ${count} times across different analyses`,
          supportingEvidence: [],
          confidence: Math.min(count / 10, 1),
          importance: count >= 5 ? 'high' : 'medium',
          timestamp: new Date().toISOString(),
        });
      });

    return patterns;
  }

  private static buildReportContext(session: ResearchSession): string {
    return `
Research Context:
- Data Sources: ${session.dataSources.map(ds => ds.name).join(', ')}
- Total Analyses: ${session.analyses.length}
- Key Insights: ${session.insights.map(i => i.title).join('; ')}

Please structure the report with:
1. Executive Summary
2. Methodology
3. Key Findings (based on insights)
4. Supporting Evidence
5. Recommendations
6. Conclusions
    `;
  }

  private static convertToResearchReport(
    techDocResponse: any,
    session: ResearchSession
  ): ResearchReport {
    // Extract content from the response
    const content = techDocResponse.technicalDocumentation || techDocResponse.finalDocumentation || '';
    const sections = content.split(/\n## /).slice(1).map((section: string) => {
      const [title, ...contentParts] = section.split('\n');
      return {
        id: `section-${title}`,
        title,
        content: contentParts.join('\n').trim(),
      };
    });

    return {
      id: `report-${Date.now()}`,
      title: 'Technical Research Report',
      executiveSummary: sections.find((s: any) => 
        s.title.toLowerCase().includes('summary') || s.title.toLowerCase().includes('overview')
      )?.content || content.split('\n\n')[0] || 'No summary available',
      methodology: 'Technical documentation analysis and synthesis',
      findings: sections,
      insights: session.insights,
      recommendations: [],
      conclusions: sections[sections.length - 1]?.content || 'See findings above',
      generatedAt: new Date().toISOString(),
      format: 'markdown',
    };
  }

  private static convertJournalismToReport(
    journalismResponse: any,
    session: ResearchSession
  ): ResearchReport {
    // Extract article content
    const article = journalismResponse.finalArticle || '';
    const sections = article.split(/\n## /).slice(1).map((section: string) => {
      const [title, ...contentParts] = section.split('\n');
      return {
        id: `section-${title}`,
        title,
        content: contentParts.join('\n').trim(),
      };
    });

    return {
      id: `report-${Date.now()}`,
      title: article.split('\n')[0].replace(/^#\s*/, '') || 'Research Report',
      executiveSummary: article.split('\n\n')[1] || 'No summary available',
      methodology: 'Investigative journalism methodology with fact-checking',
      findings: sections,
      insights: session.insights,
      recommendations: [],
      conclusions: sections[sections.length - 1]?.content || 'See findings above',
      generatedAt: new Date().toISOString(),
      format: 'markdown',
    };
  }

  private static convertBlogToReport(
    blogResponse: any,
    session: ResearchSession
  ): ResearchReport {
    // Parse blog content into sections
    const content = blogResponse.finalBlogPost || '';
    const sections = content.split(/\n## /).slice(1).map((section: string) => {
      const [title, ...contentParts] = section.split('\n');
      return {
        id: `section-${title}`,
        title,
        content: contentParts.join('\n').trim(),
      };
    });

    return {
      id: `report-${Date.now()}`,
      title: content.split('\n')[0].replace(/^#\s*/, '') || 'Research Report',
      executiveSummary: content.split('\n\n')[1] || 'No summary available',
      methodology: 'Research synthesis and analysis',
      findings: sections,
      insights: session.insights,
      recommendations: [],
      conclusions: sections[sections.length - 1]?.content || 'See findings above',
      generatedAt: new Date().toISOString(),
      format: 'markdown',
    };
  }

  // Session Management
  static getCurrentSession(projectId: string): ResearchSession | null {
    const sessions = this.getAllSessions();
    return sessions.find(s => s.projectId === projectId && s.status === 'active') || null;
  }

  static createSession(project: ResearchProject): ResearchSession {
    const session: ResearchSession = {
      id: `research-session-${Date.now()}`,
      projectId: project.id,
      phase: 'collection',
      status: 'active',
      startedAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      dataSources: [...project.dataSources],
      analyses: [],
      insights: [],
    };

    this.saveSession(session);
    return session;
  }

  static saveSession(session: ResearchSession): void {
    const sessions = this.getAllSessions();
    const index = sessions.findIndex(s => s.id === session.id);
    
    if (index >= 0) {
      sessions[index] = session;
    } else {
      sessions.push(session);
    }

    session.updatedAt = new Date().toISOString();
    localStorage.setItem(RESEARCH_SESSION_KEY, JSON.stringify(sessions));
  }

  static getAllSessions(): ResearchSession[] {
    const stored = localStorage.getItem(RESEARCH_SESSION_KEY);
    return stored ? JSON.parse(stored) : [];
  }

  static deleteSession(sessionId: string): void {
    const sessions = this.getAllSessions();
    const filtered = sessions.filter(s => s.id !== sessionId);
    localStorage.setItem(RESEARCH_SESSION_KEY, JSON.stringify(filtered));
  }

  // Extract artifacts from agent results
  static extractArtifacts(result: ResearchAgentResult): ProjectArtifact[] {
    const artifacts: ProjectArtifact[] = [];

    if (result.data.analysis) {
      artifacts.push({
        id: `artifact-analysis-${Date.now()}`,
        type: 'text',
        name: `${result.agentType} Analysis`,
        content: result.data.analysis,
        createdBy: result.agentType,
      });
    }

    if (result.data.summary) {
      artifacts.push({
        id: `artifact-summary-${Date.now()}`,
        type: 'text',
        name: `${result.agentType} Summary`,
        content: result.data.summary,
        createdBy: result.agentType,
      });
    }

    if (result.data.insights && result.data.insights.length > 0) {
      artifacts.push({
        id: `artifact-insights-${Date.now()}`,
        type: 'data',
        name: `${result.agentType} Insights`,
        data: result.data.insights,
        createdBy: result.agentType,
      });
    }

    if (result.data.rawData) {
      artifacts.push({
        id: `artifact-report-${Date.now()}`,
        type: 'document',
        name: 'Research Report',
        data: result.data.rawData,
        createdBy: result.agentType,
      });
    }

    return artifacts;
  }
}